/** Parse Opportunity History (FID 1714) → Set of invited rep IDs. */
export function parseOpportunityHistory(str) {
  if (!str) return new Set();
  const ids = new Set();
  String(str).split('\n').forEach(line => {
    const trimmed = line.trim();
    if (!trimmed) return;
    const bracketEnd = trimmed.lastIndexOf(']');
    const data = bracketEnd >= 0 ? trimmed.substring(bracketEnd + 1).trim() : trimmed;
    const id = data.split('|')[0];
    if (id && !isNaN(id)) ids.add(parseInt(id, 10));
  });
  return ids;
}

/** Parse Nearby Field Reps JSON (FID 1711) → [{ id, dist, manual }]. */
export function parseNearbyReps(json) {
  if (!json) return [];
  try {
    const arr = typeof json === 'string' ? JSON.parse(json) : json;
    if (!Array.isArray(arr)) return [];
    return arr.map(e => ({
      id: parseInt(e?.id, 10),
      dist: e?.dist == null || e?.dist === '' ? null : parseFloat(e.dist),
      manual: e?.manual === true,
    }));
  } catch {
    return [];
  }
}

/** Parse Preferred Techs JSON (FID 1730) → Map<repId, travel|null>. */
export function parsePreferredTechs(input) {
  const map = new Map();
  if (!input || (typeof input === 'string' && !input.trim())) return map;
  let data;
  try {
    data = typeof input === 'string' ? JSON.parse(input) : input;
  } catch {
    return map;
  }
  const techs = data && Array.isArray(data.techs) ? data.techs : [];
  techs.forEach(t => {
    if (!t || t.id === undefined || t.id === null || t.id === '') return;
    const id = parseInt(t.id, 10);
    if (Number.isNaN(id)) return;
    const travel = parseFloat(t.travel);
    map.set(id, Number.isFinite(travel) ? travel : null);
  });
  return map;
}

/** Rep FID 248–250 block this ticket's client. `clientName` may be raw or already lowercased. */
export function isClientBlocked(rep, clientName) {
  const name = String(clientName || '').trim().toLowerCase();
  if (!name || !rep) return false;
  const blocked = [rep.noInviteClient1, rep.noInviteClient2, rep.noInviteClient3]
    .filter(Boolean)
    .map(s => String(s).trim().toLowerCase());
  return blocked.includes(name);
}

/**
 * Reps who qualify for one ticket's location.
 * Same hard filters as the single-ticket opportunity list: nearby or preferred,
 * not No Inviting, not client-blocked. FN-not-ready is flagged, not removed.
 * Reps who appear only in opportunity history are omitted — history is not location.
 */
export function locationInviteesForTicket(ticket, repsById) {
  const clientName = String(ticket?.clientName || '').trim().toLowerCase();
  const fnReady = String(ticket?.fnReadiness || '').toLowerCase().includes('ready');
  const nearby = parseNearbyReps(ticket?.nearbyFieldRepsJson).filter(e => Number.isFinite(e.id));
  const preferredMap = parsePreferredTechs(ticket?.preferredTechsJson);
  const invited = parseOpportunityHistory(ticket?.opportunityHistory);

  const nearbyIdSet = new Set(nearby.map(r => r.id));
  const extraPreferred = [...preferredMap.keys()]
    .filter(id => !nearbyIdSet.has(id))
    .map(id => ({ id, dist: null, manual: true }));

  const out = [];
  for (const entry of [...nearby, ...extraPreferred]) {
    const rep = repsById.get(entry.id);
    if (!rep || rep.noInviting === true) continue;
    if (isClientBlocked(rep, clientName)) continue;
    out.push({
      rep,
      dist: Number.isFinite(entry.dist) ? entry.dist : null,
      preferred: preferredMap.has(entry.id),
      prefTravel: preferredMap.get(entry.id) ?? null,
      fnBlocked: !fnReady && rep.paymentMethod === 'FN',
      alreadyInvited: invited.has(entry.id),
    });
  }
  return out;
}

function ticketHasLocationCandidates(ticket) {
  const nearby = parseNearbyReps(ticket?.nearbyFieldRepsJson).some(e => Number.isFinite(e.id));
  return nearby || parsePreferredTechs(ticket?.preferredTechsJson).size > 0;
}

/**
 * Merge per-ticket location invitees across a selection.
 * `qualifyCount` is how many of the passed tickets that rep qualifies for.
 */
export function aggregateLocationInvitees(tickets, fieldReps) {
  const repsById = new Map();
  (fieldReps || []).forEach(r => {
    if (r && r.repId != null) repsById.set(r.repId, r);
  });

  const list = tickets || [];
  const perTicket = list.map(ticket => ({
    ticket,
    invitees: locationInviteesForTicket(ticket, repsById),
    hasCandidates: ticketHasLocationCandidates(ticket),
  }));

  const byRep = new Map();
  for (const { ticket, invitees } of perTicket) {
    for (const inv of invitees) {
      let row = byRep.get(inv.rep.repId);
      if (!row) {
        row = { rep: inv.rep, sites: [] };
        byRep.set(inv.rep.repId, row);
      }
      row.sites.push({
        recordId: ticket.recordId,
        siteName: ticket.siteName || `Ticket #${ticket.recordId}`,
        city: ticket.city || '',
        state: ticket.state || '',
        dist: inv.dist,
        fnBlocked: inv.fnBlocked,
        alreadyInvited: inv.alreadyInvited,
        preferred: inv.preferred,
        prefTravel: inv.prefTravel,
      });
    }
  }

  const siteCount = perTicket.length;
  return {
    siteCount,
    rows: [...byRep.values()].map(row => ({
      ...row,
      qualifyCount: row.sites.length,
      siteCount,
    })),
    missingLocation: perTicket
      .filter(p => !p.hasCandidates)
      .map(p => ({
        recordId: p.ticket.recordId,
        siteName: p.ticket.siteName || `Ticket #${p.ticket.recordId}`,
      })),
  };
}
